import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BookingTabs from "./BookingTabs";
import BookingsTable from "./BookingsTable";
import SubRequestsTable from "./SubRequestsTable";
import AuthContext from "../../contexts/AuthProvider";
import { API_URL } from "../../utils/config";
import "./Booking.css";

const baseUrl = API_URL;

const Bookings = () => {
  const { t } = useTranslation("booking");
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("bookings");
  const [bookingsCount, setBookingsCount] = useState(0);
  const [subRequestsCount, setSubRequestsCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("allStatus");
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useContext(AuthContext);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Reset filter when switching tabs
    setStatusFilter("allStatus");
  };

  const handleStatusFilterChange = (filter) => {
    setStatusFilter(filter);
  };

  const handleBookingsCountChange = (count) => {
    setBookingsCount(count);
  };

  const handleSubRequestsCountChange = (count) => {
    setSubRequestsCount(count);
  };

  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };

  const fetchBookingsCount = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return 0;
      }

      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/bookings?page=1`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        navigate("/login");
        return 0;
      }
      if (!response.ok) {
        return 0;
      }

      const json = await response.json();
      return json.totalItems || 0;
    } catch (error) {
      console.error("Error fetching bookings count:", error);
      return 0;
    }
  };

  const fetchSubRequestsCount = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return 0;
      }

      const response = await fetch(`${baseUrl}/api/v1/attnd-venue/subOrders?page=1`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          lang: getLangFromLocalStorage(),
        },
      });

      if (response.status === 401) {
        navigate("/login");
        return 0;
      }
      if (!response.ok) {
        return 0;
      }

      const json = await response.json();
      return json.totalItems || 0;
    } catch (error) {
      console.error("Error fetching sub-requests count:", error);
      return 0;
    }
  };

  useEffect(() => {
    const fetchCounts = async () => {
      setLoading(true);
      try {
        const [bookingsTotal, subRequestsTotal] = await Promise.all([
          fetchBookingsCount(),
          fetchSubRequestsCount()
        ]);
        
        setBookingsCount(bookingsTotal);
        setSubRequestsCount(subRequestsTotal);
      } catch (error) {
        console.error("Error fetching counts:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchCounts();
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="booking">
      <div className="bookings-container">
        {/* Heading */}
        <h2 className="booking-heading">{t("heading")}</h2>
        <p className="booking-desc">{t("description")}</p>

        {/* Tabs */}
        <BookingTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          bookingsCount={bookingsCount}
          subRequestsCount={subRequestsCount}
          statusFilter={statusFilter}
          onStatusFilterChange={handleStatusFilterChange}
        />

        {/* Conditional Content */}
        {!loading && (
          activeTab === "bookings" ? (
            <BookingsTable 
              onCountChange={handleBookingsCountChange}
              statusFilter={statusFilter}
              skipInitialCountUpdate={true}
            />
          ) : (
            <SubRequestsTable 
              onCountChange={handleSubRequestsCountChange}
              statusFilter={statusFilter}
              skipInitialCountUpdate={true}
            />
          )
        )}
      </div>
    </div>
  );
};

export default Bookings;
